import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getGame, updateGame } from '../services/games';
import { createFrame, updateFrame } from '../services/frames';
import { createBreak, getBreaksByFrame } from '../services/breaks';
import { getMatchByGameId, recordMatchResult } from '../services/tournamentsV2';
import { supabase } from '../lib/supabase';
// import { subscribeToGameUpdates, unsubscribe } from '../services/realtime'; // TEMPORARILY DISABLED
import { GameWithPlayers } from '../types';
import Loading from '../components/Loading';
import './GamePlay.css';

const GamePlay: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isTableMode = sessionStorage.getItem('table_mode') === 'true';
  const [game, setGame] = useState<GameWithPlayers | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentFrame, setCurrentFrame] = useState(1);
  const [currentPlayer, setCurrentPlayer] = useState<'A' | 'B'>('A');
  const [frameScoreA, setFrameScoreA] = useState(0);
  const [frameScoreB, setFrameScoreB] = useState(0);
  const [isFrameComplete, setIsFrameComplete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastBallPotted, setLastBallPotted] = useState<'red' | 'colored' | null>(null);
  const [redsRemaining, setRedsRemaining] = useState(15);
  const [currentBreak, setCurrentBreak] = useState<number[]>([]);
  const [currentBreakScore, setCurrentBreakScore] = useState(0);
  const [breaks, setBreaks] = useState<Array<{player: 'A' | 'B', score: number, balls: number[], timestamp: Date, isFoul?: boolean}>>([]);
  const [showBreakHistory, setShowBreakHistory] = useState(false);
  const [highestBreakA, setHighestBreakA] = useState(0);
  const [highestBreakB, setHighestBreakB] = useState(0);
  const [foulPoints, setFoulPoints] = useState(0);
  const [breakEnded, setBreakEnded] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [frameLoading, setFrameLoading] = useState(false);
  const [currentFrameId, setCurrentFrameId] = useState<number | null>(null);
  const [showStats, setShowStats] = useState(false); // Toggle for training stats

  // Screen Wake Lock - Prevent screen from sleeping during game
  useEffect(() => {
    let wakeLock: any = null;

    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await (navigator as any).wakeLock.request('screen');
          console.log('Screen Wake Lock activated');
        }
      } catch (err) {
        console.log('Wake Lock not supported or denied:', err);
      }
    };

    requestWakeLock();

    // Re-acquire wake lock when page becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && wakeLock !== null) {
        requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLock !== null) {
        wakeLock.release().then(() => {
          console.log('Screen Wake Lock released');
        });
      }
    };
  }, []);

  useEffect(() => {
    if (id) {
      loadGame(parseInt(id));
    }
  }, [id]);

  // Realtime subscription for live updates
  // ⚠️ TEMPORARILY DISABLED - Causing issues with scoring state
  // TODO: Re-enable after fixing state management issues
  /*
  useEffect(() => {
    if (!id || !game) return;

    console.log(`Setting up realtime subscription for game ${id}`);
    
    const channel = subscribeToGameUpdates(
      parseInt(id),
      // On break update
      (payload) => {
        console.log('Realtime break update received:', payload);
        // Only reload breaks if this is a remote update (not from current user)
        if (currentFrameId && payload.eventType !== 'DELETE') {
          loadFrameBreaks(currentFrameId);
        }
      },
      // On frame update
      (payload) => {
        console.log('Realtime frame update received:', payload);
        // Only reload if frame was completed by another user
        // Don't reload during active play to avoid resetting local state
        if (payload.new?.playerAFinalScore !== null && payload.new?.playerBFinalScore !== null) {
          // Frame was completed, safe to reload
          loadGame(parseInt(id));
        }
      },
      // On game update
      (payload) => {
        console.log('Realtime game update received:', payload);
        // Only update game metadata, don't reset playing state
        if (payload.new) {
          setGame(prev => prev ? {
            ...prev,
            framesPlayerA: payload.new.framesPlayerA || prev.framesPlayerA,
            framesPlayerB: payload.new.framesPlayerB || prev.framesPlayerB,
            status: payload.new.status || prev.status
          } : null);
        }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      console.log(`Cleaning up realtime subscription for game ${id}`);
      unsubscribe(channel);
    };
  }, [id, game, currentFrameId]);
  */

  // Update canUndo based on current break
  useEffect(() => {
    setCanUndo(currentBreak.length > 0);
  }, [currentBreak]);

  // Load or create frame when game is loaded
  useEffect(() => {
    const loadOrCreateFrame = async () => {
      if (game && !currentFrameId && !frameLoading) {
        setFrameLoading(true);
        try {
          // First, check if there's an ongoing frame (either score is null)
          const { data: openFrames, error } = await supabase
            .from('frames')
            .select('*')
            .eq('gameId', game.id)
            .or('playerAFinalScore.is.null,playerBFinalScore.is.null')
            .order('created_at', { ascending: false })
            .limit(1);

          if (error) {
            console.error('Error checking for open frames:', error);
            throw error;
          }

          if (openFrames && openFrames.length > 0) {
            // Continue existing ongoing frame (even if it has no breaks)
            console.log('Continuing ongoing frame:', openFrames[0]);
            setCurrentFrameId(openFrames[0].id);
            
            // Load breaks for this frame to recreate state (will be empty if no breaks)
            await loadFrameBreaks(openFrames[0].id);
          } else {
            // Create new frame only if no ongoing frame exists
            console.log('Creating new frame for game:', game.id);
            const newFrame = await createFrame(game.id);
            console.log('Frame created:', newFrame);
            setCurrentFrameId(newFrame.id);
          }
        } catch (frameErr) {
          console.error('Failed to load/create frame:', frameErr);
          setError('Failed to load/create frame');
        } finally {
          setFrameLoading(false);
        }
      }
    };

    loadOrCreateFrame();
  }, [game, currentFrameId, frameLoading]); // Added frameLoading to prevent duplicate calls

  // Load global highest breaks from all frames
  const loadGlobalHighestBreaks = async () => {
    try {
      const { data: allBreaksData, error: allBreaksError } = await supabase
        .from('breaks')
        .select('*')
        .eq('gameId', game!.id)
        .order('timestamp', { ascending: true });

      if (!allBreaksError && allBreaksData) {
        let globalHighestBreakA = 0;
        let globalHighestBreakB = 0;
        
        for (const breakItem of allBreaksData) {
          const breakScore = breakItem.sequence.reduce((sum: number, ball: number) => {
            return sum + (ball > 0 ? ball : 0);
          }, 0);
          
          if (breakItem.player === 'A' && breakScore > globalHighestBreakA) {
            globalHighestBreakA = breakScore;
          } else if (breakItem.player === 'B' && breakScore > globalHighestBreakB) {
            globalHighestBreakB = breakScore;
          }
        }
        
        setHighestBreakA(globalHighestBreakA);
        setHighestBreakB(globalHighestBreakB);
      }
    } catch (err) {
      console.error('Failed to load global highest breaks:', err);
    }
  };

  // Check if game is completed and sync with tournament match
  useEffect(() => {
    const syncTournamentResult = async () => {
      if (game && game.status === '2') { // Game completed
        try {
          // Check if this is a tournament match
          const tournamentMatch = await getMatchByGameId(game.id);
          
          if (tournamentMatch) {
            console.log('Game completed, syncing with tournament match:', tournamentMatch.id);
            
            // Determine winner (1 = playerA, 2 = playerB)
            const winnerSlot = game.framesPlayerA > game.framesPlayerB ? 1 : 2;
            
            // Record the tournament match result
            await recordMatchResult(
              tournamentMatch.id,
              winnerSlot,
              game.framesPlayerA,
              game.framesPlayerB
            );
            
            console.log('Tournament match result recorded successfully');
          }
        } catch (err) {
          console.error('Error syncing tournament result:', err);
          // Don't show error to user - tournament sync is background operation
        }
      }
    };

    syncTournamentResult();
  }, [game?.status]);

  // Load breaks for a frame and recreate game state
  const loadFrameBreaks = async (frameId: number) => {
    try {
      console.log('Loading breaks for frame:', frameId);
      const frameBreaks = await getBreaksByFrame(frameId);
      console.log('Loaded breaks:', frameBreaks);
      
      // First, load all completed frames to get total game scores
      const { data: allFrames, error: framesError } = await supabase
        .from('frames')
        .select('*')
        .eq('gameId', game!.id)
        .not('playerAFinalScore', 'is', null)
        .not('playerBFinalScore', 'is', null)
        .order('created_at', { ascending: true });

      if (framesError) {
        console.error('Error loading completed frames:', framesError);
        throw framesError;
      }

      // Calculate completed frames count and total scores from completed frames
      let completedFramesCount = 0;
      let totalPlayerAScore = 0;
      let totalPlayerBScore = 0;
      if (allFrames) {
        completedFramesCount = allFrames.length;
        for (const frame of allFrames) {
          totalPlayerAScore += frame.playerAFinalScore || 0;
          totalPlayerBScore += frame.playerBFinalScore || 0;
        }
      }
      
      // Recreate current frame state from breaks
      let playerAScore = 0;
      let playerBScore = 0;
      let lastPlayer = 'A';
      let redsRemaining = 15;
      let lastBallPotted: 'red' | 'colored' | null = null;
      const allBreaks = [];
      let highestBreakA = 0;
      let highestBreakB = 0;

      // If no breaks in this ongoing frame, start fresh
      if (frameBreaks.length === 0) {
        console.log('No breaks in current ongoing frame, starting fresh');
        setFrameScoreA(0);
        setFrameScoreB(0);
        setCurrentPlayer('A');
        setRedsRemaining(15);
        setLastBallPotted(null);
        setBreaks([]);
        setFoulPoints(0);
        setBreakEnded(false);
        setCanUndo(false);
        
        // Still load global highest breaks from all frames
        await loadGlobalHighestBreaks();
        
        // Update the game object with correct frame counts for empty frame
        if (game) {
          const updatedGame = {
            ...game,
            framesPlayerA: completedFramesCount > 0 ? 
              allFrames.filter(f => f.playerAFinalScore > f.playerBFinalScore).length : 0,
            framesPlayerB: completedFramesCount > 0 ? 
              allFrames.filter(f => f.playerBFinalScore > f.playerAFinalScore).length : 0
          };
          setGame(updatedGame);
          
          // Update current frame number (completed frames + 1 for current ongoing frame)
          setCurrentFrame(completedFramesCount + 1);
        }
        
        return; // Exit early for empty frame
      }

      for (const breakItem of frameBreaks) {
        // Add to local breaks array (show sum of all points in break, excluding foul points)
        const breakScore = breakItem.sequence.reduce((sum, ball) => {
          return sum + (ball > 0 ? ball : 0); // Only count positive ball values, ignore fouls
        }, 0);
        
        allBreaks.push({
          player: breakItem.player,
          score: breakScore, // Sum of all balls potted (excluding foul points)
          balls: breakItem.sequence,
          timestamp: breakItem.timestamp,
          isFoul: breakItem.isFoul
        });

        // Update scores and highest break (count break score for highest break, even if ended in foul)
        if (breakItem.player === 'A') {
          playerAScore += breakScore; // Count break score for frame score
          if (breakScore > highestBreakA) {
            highestBreakA = breakScore; // Count break score for highest break
          }
        } else {
          playerBScore += breakScore; // Count break score for frame score
          if (breakScore > highestBreakB) {
            highestBreakB = breakScore; // Count break score for highest break
          }
        }
        
        // Add foul points to opponent (if any)
        if (breakItem.isFoul) {
          if (breakItem.player === 'A') {
            playerBScore += breakItem.foulPoints;
          } else {
            playerAScore += breakItem.foulPoints;
          }
        }

        // Update game state
        lastPlayer = breakItem.player;
        
        // Count reds potted in this break and subtract from remaining
        const redsPotted = breakItem.sequence.filter(ball => ball === 1).length;
        redsRemaining = Math.max(0, redsRemaining - redsPotted);
        
        // Determine last ball potted
        if (breakItem.sequence.length > 0) {
          const lastBall = breakItem.sequence[breakItem.sequence.length - 1];
          if (lastBall === 1) {
            lastBallPotted = 'red';
          } else if (lastBall > 1) {
            lastBallPotted = 'colored';
          }
        }
      }

      // Update state with recreated data
      setFrameScoreA(playerAScore);
      setFrameScoreB(playerBScore);
      setCurrentPlayer(lastPlayer === 'A' ? 'B' : 'A'); // Next player to play
      setRedsRemaining(redsRemaining);
      // Reset lastBallPotted to null when switching to next player (like switchPlayer does)
      setLastBallPotted(null);
      setBreaks(allBreaks);
      setHighestBreakA(highestBreakA);
      setHighestBreakB(highestBreakB);
      
      // Load global highest breaks from all frames
      await loadGlobalHighestBreaks();
      
      // Update the game object with correct frame counts
      if (game) {
        const updatedGame = {
          ...game,
          framesPlayerA: completedFramesCount > 0 ? 
            allFrames.filter(f => f.playerAFinalScore > f.playerBFinalScore).length : 0,
          framesPlayerB: completedFramesCount > 0 ? 
            allFrames.filter(f => f.playerBFinalScore > f.playerAFinalScore).length : 0
        };
        setGame(updatedGame);
        
        // Update current frame number (completed frames + 1 for current ongoing frame)
        setCurrentFrame(completedFramesCount + 1);
      }
      
      console.log('Game state recreated:', {
        currentFramePlayerAScore: playerAScore,
        currentFramePlayerBScore: playerBScore,
        completedFramesCount,
        totalPlayerAScore,
        totalPlayerBScore,
        currentPlayer: lastPlayer === 'A' ? 'B' : 'A',
        redsRemaining,
        lastBallPotted,
        breaksCount: allBreaks.length
      });
      
    } catch (err) {
      console.error('Failed to load frame breaks:', err);
      setError('Failed to load frame breaks');
    }
  };

  const loadGame = async (gameId: number) => {
    try {
      setLoading(true);
      setError(null);
      const gameData = await getGame(gameId);
      setGame(gameData);
      
      // Calculate current frame based on completed frames
      const totalFrames = gameData.framesPlayerA + gameData.framesPlayerB;
      setCurrentFrame(totalFrames + 1);
      
      // Reset frame scores for new frame
      setFrameScoreA(0);
      setFrameScoreB(0);
      setIsFrameComplete(false);
      setLastBallPotted(null);
      setRedsRemaining(15);
      setCurrentBreak([]);
      setCurrentBreakScore(0);
      setBreaks([]);
      setHighestBreakA(0);
      setHighestBreakB(0);
      setFoulPoints(0);
      setBreakEnded(false);
      setCanUndo(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load game');
    } finally {
      setLoading(false);
    }
  };

  const addFoulPoints = (points: number) => {
    if (isFrameComplete || breakEnded) return;

    // Balls 1-4 (red, yellow, green, brown) count as 4 foul points
    const foulPoints = points <= 4 ? 4 : points;

    // Add foul points to the other player
    if (currentPlayer === 'A') {
      setFrameScoreB(prev => prev + foulPoints);
    } else {
      setFrameScoreA(prev => prev + foulPoints);
    }

    // Add to foul points counter
    setFoulPoints(prev => prev + foulPoints);

    // Add foul to current break as the last element (negative value to indicate foul)
    // Store original ball value but display foul points
    setCurrentBreak(prev => [...prev, -points]);
    setCurrentBreakScore(prev => prev + foulPoints);

    // End the break - no more points can be added
    setBreakEnded(true);

    // Clear any previous error
    setError(null);
  };

  const undoLastShot = () => {
    if (currentBreak.length === 0) return;

    const lastBall = currentBreak[currentBreak.length - 1];
    const isFoul = lastBall < 0;
    const ballValue = Math.abs(lastBall);

    // Remove the last ball from the break
    setCurrentBreak(prev => prev.slice(0, -1));

    if (isFoul) {
      // Undo foul points
      const foulPoints = ballValue <= 4 ? 4 : ballValue;
      
      // Remove foul points from the other player
      if (currentPlayer === 'A') {
        setFrameScoreB(prev => prev - foulPoints);
      } else {
        setFrameScoreA(prev => prev - foulPoints);
      }

      // Remove from foul points counter
      setFoulPoints(prev => prev - foulPoints);

      // Remove from current break score
      setCurrentBreakScore(prev => prev - foulPoints);

      // Always restore break state when undoing a foul
      setBreakEnded(false);

      // Update last ball potted based on what's left in the break after removing the foul
      const remainingBreak = currentBreak.slice(0, -1);
      if (remainingBreak.length === 0) {
        // No balls left, reset to initial state
        setLastBallPotted(null);
      } else {
        // Check the last remaining ball to determine what can be potted next
        const lastRemainingBall = remainingBreak[remainingBreak.length - 1];
        if (lastRemainingBall === 1) {
          // Last ball was red, next should be colored
          setLastBallPotted('red');
        } else {
          // Last ball was colored, next should be red (if reds remain)
          setLastBallPotted('colored');
        }
      }
    } else {
      // Undo regular points
      setCurrentBreakScore(prev => prev - ballValue);

      // Update reds remaining if it was a red ball
      if (ballValue === 1) {
        setRedsRemaining(prev => prev + 1);
      }

      // Update last ball potted based on what's left in the break
      const remainingBreak = currentBreak.slice(0, -1);
      if (remainingBreak.length === 0) {
        // No balls left, reset to initial state
        setLastBallPotted(null);
      } else {
        // Check the last remaining ball to determine what can be potted next
        const lastRemainingBall = remainingBreak[remainingBreak.length - 1];
        if (lastRemainingBall === 1) {
          // Last ball was red, next should be colored
          setLastBallPotted('red');
        } else {
          // Last ball was colored, next should be red (if reds remain)
          setLastBallPotted('colored');
        }
      }
    }

    // Clear any previous error
    setError(null);
  };

  const addPoints = (points: number) => {
    if (isFrameComplete || breakEnded) return;

    // Determine if this is a red ball or colored ball
    const isRedBall = points === 1;
    const isColoredBall = points >= 2 && points <= 7;

    // Check snooker ball order rules
    if (isRedBall) {
      // Can only pot red if:
      // 1. It's the first shot of the frame, OR
      // 2. Last ball potted was a colored ball, OR
      // 3. No reds remaining (end game)
      if (lastBallPotted === 'colored' || lastBallPotted === null || redsRemaining === 0) {
        if (redsRemaining > 0) {
          setRedsRemaining(prev => prev - 1);
        }
        setLastBallPotted('red');
      } else {
        // Invalid shot - show error
        setError('You must pot a colored ball after potting a red ball');
        return;
      }
    } else if (isColoredBall) {
      // Can only pot colored ball if:
      // 1. Last ball potted was a red ball, OR
      // 2. No reds remaining (end game)
      if (lastBallPotted === 'red' || redsRemaining === 0) {
        setLastBallPotted('colored');
      } else {
        // Invalid shot - show error
        setError('You must pot a red ball first');
        return;
      }
    }

    // Add points to current player
    if (currentPlayer === 'A') {
      setFrameScoreA(prev => prev + points);
    } else {
      setFrameScoreB(prev => prev + points);
    }

    // Update current break
    setCurrentBreak(prev => [...prev, points]);
    setCurrentBreakScore(prev => prev + points);

    // Clear any previous error
    setError(null);
  };

  const switchPlayer = async () => {
    // Save current break if there are any points
    console.log('Switch Player - Current Break:', currentBreak);
    console.log('Switch Player - Current Frame ID:', currentFrameId);
    console.log('Switch Player - Game ID:', game?.id);
    
    if (currentBreak.length > 0 && currentFrameId) {
      try {
        // Save to database using the updated schema
        const breakData = {
          frameId: currentFrameId,
          gameId: game!.id,
          player: currentPlayer,
          sequence: [...currentBreak], // JSON array of ball values
          score: currentBreakScore,
          isFoul: breakEnded,
          foulBall: breakEnded ? Math.abs(currentBreak[currentBreak.length - 1]) : undefined,
          foulPoints: foulPoints,
          breakEnded: breakEnded,
          redsRemaining: redsRemaining,
          timestamp: new Date()
        };
        
        console.log('Saving break data:', breakData);
        const result = await createBreak(breakData);
        console.log('Break saved successfully:', result);
        
        // Also save to local state for display
        const breakScore = currentBreak.reduce((sum, ball) => {
          return sum + (ball > 0 ? ball : 0); // Only count positive ball values, ignore fouls
        }, 0);
        
        const newBreak = {
          player: currentPlayer,
          score: breakScore, // Sum of all balls potted (excluding foul points)
          balls: [...currentBreak],
          timestamp: new Date(),
          isFoul: breakEnded
        };
        setBreaks(prev => [...prev, newBreak]);
        
        // Update highest break (count break score even if ended in foul)
        if (currentPlayer === 'A' && breakScore > highestBreakA) {
          setHighestBreakA(breakScore);
        } else if (currentPlayer === 'B' && breakScore > highestBreakB) {
          setHighestBreakB(breakScore);
        }
      } catch (err) {
        console.error('Failed to save break:', err);
        setError('Failed to save break');
      }
    }
    
    setCurrentPlayer(prev => prev === 'A' ? 'B' : 'A');
    // Reset ball order when switching players
    setLastBallPotted(null);
    setError(null);
    // Reset current break
    setCurrentBreak([]);
    setCurrentBreakScore(0);
    setFoulPoints(0);
    setBreakEnded(false);
    setCanUndo(false);
  };

  const completeFrame = async () => {
    if (isFrameComplete || !currentFrameId) return;
    
    // Check if scores are equal - frame cannot be completed
    if (frameScoreA === frameScoreB) {
      setError('Frame cannot be completed with equal scores. Continue playing to break the tie.');
      return;
    }

    setSaving(true);
    try {
      // Save current break if there are any points
      if (currentBreak.length > 0) {
        const breakData = {
          frameId: currentFrameId,
          gameId: game!.id,
          player: currentPlayer,
          sequence: [...currentBreak],
          score: currentBreakScore,
          isFoul: breakEnded,
          foulBall: breakEnded ? Math.abs(currentBreak[currentBreak.length - 1]) : undefined,
          foulPoints: foulPoints,
          breakEnded: breakEnded,
          redsRemaining: redsRemaining,
          timestamp: new Date()
        };
        
        await createBreak(breakData);
        
        // Also save to local state for display
        const breakScore = currentBreak.reduce((sum, ball) => {
          return sum + (ball > 0 ? ball : 0); // Only count positive ball values, ignore fouls
        }, 0);
        
        const newBreak = {
          player: currentPlayer,
          score: breakScore, // Sum of all balls potted (excluding foul points)
          balls: [...currentBreak],
          timestamp: new Date(),
          isFoul: breakEnded
        };
        setBreaks(prev => [...prev, newBreak]);
        
        // Update highest break (count break score even if ended in foul)
        if (currentPlayer === 'A' && breakScore > highestBreakA) {
          setHighestBreakA(breakScore);
        } else if (currentPlayer === 'B' && breakScore > highestBreakB) {
          setHighestBreakB(breakScore);
        }
      }

      // Close current frame by setting final scores
      await updateFrame(currentFrameId, {
        playerAFinalScore: frameScoreA,
        playerBFinalScore: frameScoreB
      });

      // Update game with new frame scores
      const winner = frameScoreA > frameScoreB ? 'A' : 'B';
      const updates = {
        framesPlayerA: game!.framesPlayerA + (winner === 'A' ? 1 : 0),
        framesPlayerB: game!.framesPlayerB + (winner === 'B' ? 1 : 0)
      };

      await updateGame(game!.id, updates);
      setGame(prev => prev ? { ...prev, ...updates } : null);
      
      // Check if game is complete
      const totalFrames = updates.framesPlayerA + updates.framesPlayerB;
      const framesToPlay = game!.framesToPlay;
      const playerAWins = updates.framesPlayerA;
      const playerBWins = updates.framesPlayerB;
      
      if (totalFrames >= framesToPlay || playerAWins > framesToPlay / 2 || playerBWins > framesToPlay / 2) {
        // Game is complete
        setIsFrameComplete(true);
        
        // Check if this is a tournament match and report result
        try {
          const tournamentMatch = await getMatchByGameId(game!.id);
          
          if (tournamentMatch) {
            console.log('Game completed, syncing with tournament match:', tournamentMatch.id);
            
            // Determine winner (1 = playerA, 2 = playerB)
            const winnerSlot = updates.framesPlayerA > updates.framesPlayerB ? 1 : 2;
            
            // Record the tournament match result
            await recordMatchResult(
              tournamentMatch.id,
              winnerSlot,
              updates.framesPlayerA,
              updates.framesPlayerB
            );
            
            console.log('Tournament match result recorded successfully');
            
            // Only show alert for non-table mode
            if (!isTableMode) {
              alert(`Game Complete!\n\n${game!.playerA.preName} ${game!.playerA.name}: ${updates.framesPlayerA} - ${updates.framesPlayerB} : ${game!.playerB.preName} ${game!.playerB.name}\n\nTournament has been updated!`);
            }
          } else {
            // Only show alert for non-table mode
            if (!isTableMode) {
              alert(`Game Complete! ${game!.playerA.preName} ${game!.playerA.name}: ${updates.framesPlayerA} - ${updates.framesPlayerB} : ${game!.playerB.preName} ${game!.playerB.name}`);
            }
          }
        } catch (err) {
          console.error('Error syncing tournament result:', err);
          
          // Only show alert for non-table mode
          if (!isTableMode) {
            alert(`Game Complete! ${game!.playerA.preName} ${game!.playerA.name}: ${updates.framesPlayerA} - ${updates.framesPlayerB} : ${game!.playerB.preName} ${game!.playerB.name}\n\n(Warning: Failed to update tournament)`);
          }
        }
        
        // Navigate based on mode
        // Table mode: Don't navigate - let user stay on result screen
        // They can manually go back via "← Table Status" button
        if (!isTableMode) {
          navigate('/games');
        }
        return;
      }
      
      // Create new frame for next round
      const newFrame = await createFrame(game!.id);
      setCurrentFrameId(newFrame.id);
      
      // Reset for next frame
      setFrameScoreA(0);
      setFrameScoreB(0);
      setCurrentPlayer('A');
      setLastBallPotted(null);
      setRedsRemaining(15);
      setCurrentBreak([]);
      setCurrentBreakScore(0);
      setBreaks([]);
      setHighestBreakA(0);
      setHighestBreakB(0);
      setFoulPoints(0);
      setBreakEnded(false);
      setCanUndo(false);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save frame');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  if (error || !game) {
    return (
      <div className="game-play-container">
        <div className="error-message">
          <p>{error || 'Game not found'}</p>
          <button 
            onClick={() => {
              if (isTableMode) {
                sessionStorage.removeItem('table_mode');
                navigate('/table-view');
              } else {
                navigate('/games');
              }
            }} 
            className="btn btn-secondary"
          >
            {isTableMode ? '← Table Status' : 'Back to Games'}
          </button>
        </div>
      </div>
    );
  }

  const isGameComplete = (() => {
    // Game is complete when one player has won more than half of the frames
    const totalFramesPlayed = game.framesPlayerA + game.framesPlayerB;
    const framesToWin = Math.ceil(game.framesToPlay / 2);
    
    return game.framesPlayerA >= framesToWin || 
           game.framesPlayerB >= framesToWin || 
           totalFramesPlayed >= game.framesToPlay;
  })();

  // For completed games, show a different interface that allows viewing but not playing
  if (isGameComplete) {
    return (
      <div className="game-play-container">
        <div className="game-complete">
          <h1>Game Complete!</h1>
          <div className="final-score">
            <div className="player-score">
              <span className="player-name">{game.playerA.preName} {game.playerA.name}</span>
              <span className="score">{game.framesPlayerA}</span>
            </div>
            <div className="score-separator">:</div>
            <div className="player-score">
              <span className="score">{game.framesPlayerB}</span>
              <span className="player-name">{game.playerB.preName} {game.playerB.name}</span>
            </div>
          </div>
          <div className="game-actions">
            <button 
              onClick={() => {
                if (isTableMode) {
                  sessionStorage.removeItem('table_mode');
                  navigate('/table-view');
                } else {
                  navigate('/games');
                }
              }} 
              className="btn btn-primary"
            >
              {isTableMode ? '← Table Status' : 'Back to Games'}
            </button>
            <button 
              onClick={() => setShowBreakHistory(true)} 
              className="btn btn-secondary"
            >
              📊 View Break History
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="game-play-container">
      {/* Main Game Area - Single Column */}
      <div className="game-main-single">
        {/* Modern Snooker Scoreboard */}
        <div className="snooker-scoreboard">
          {/* Header Controls */}
          <div className="scoreboard-header">
            <div className="header-left">
              <button 
                onClick={() => {
                  if (isTableMode) {
                    sessionStorage.removeItem('table_mode');
                    navigate('/table-view');
                  } else {
                    navigate(`/game/${game.id}`);
                  }
                }} 
                className="btn btn-secondary btn-small"
              >
                ← {isTableMode ? 'Table Status' : 'Back'}
              </button>
              <button
                onClick={() => setShowBreakHistory(true)}
                className="btn btn-secondary btn-small"
              >
                📊 History
              </button>
            </div>
            <div className="header-right">
              <label className="stats-toggle">
                <input 
                  type="checkbox" 
                  checked={showStats} 
                  onChange={(e) => setShowStats(e.target.checked)}
                />
                <div className="switch-track">
                  <div className="switch-thumb"></div>
                </div>
                <span className="toggle-label">Stats</span>
              </label>
              {showStats && (
                <div className="game-stats">
                  <div className="stat-item">
                    <span className="stat-label">Diff:</span>
                    <span className="stat-value">{Math.abs(frameScoreA - frameScoreB)}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Remaining:</span>
                    <span className="stat-value">{redsRemaining * 8 + 27}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Diagonal Split Scoreboard */}
          <div className="scoreboard-main">
            {/* Player A Section */}
            <div 
              className={`player-section player-a ${currentPlayer === 'A' ? 'active' : ''}`}
              onClick={() => currentPlayer !== 'A' && !isFrameComplete && switchPlayer()}
              style={{ cursor: currentPlayer !== 'A' && !isFrameComplete ? 'pointer' : 'default' }}>
              <div className="player-name">
                <span className="name">{game.playerA.preName} {game.playerA.name}</span>
              </div>
              <div className="current-score">{frameScoreA}</div>
              <div className="break-info">
                <span className="break-label">Break</span>
                <span className="break-score">{currentPlayer === 'A' ? currentBreakScore : 0}</span>
              </div>
              {currentPlayer === 'A' && currentBreak.length > 0 && (
                <div className="break-visual">
                  <div className="break-balls-row">
                    {currentBreak.map((ball, index) => {
                      const isFoul = ball < 0;
                      const ballValue = Math.abs(ball);
                      const ballColor = ballValue === 1 ? 'red' : ballValue === 2 ? 'yellow' : ballValue === 3 ? 'green' : ballValue === 4 ? 'brown' : ballValue === 5 ? 'blue' : ballValue === 6 ? 'pink' : 'black';
                      return (
                        <div 
                          key={index} 
                          className={`break-ball-small ${isFoul ? `foul-${ballColor}` : ballColor}`}
                        >
                          {isFoul ? 'F' : ballValue}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="player-stats">
                <div className="stat-item">
                  <span className="stat-label">Highbreak</span>
                  <span className="stat-value">{highestBreakA}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Points</span>
                  <span className="stat-value">{frameScoreA}</span>
                </div>
              </div>
            </div>

            {/* Center Divider */}
            <div className="center-divider">
              <div className="divider-content">
                <div className="frame-scores">
                  <span className="frame-score">{game.framesPlayerA}</span>
                  <span className="frame-separator">({game.framesToPlay})</span>
                  <span className="frame-score">{game.framesPlayerB}</span>
                </div>
                {/* Only show game-info if table information is available */}
                {false && ( // TODO: Replace with actual table condition when available
                  <div className="game-info">
                    <span className="table-info">Table</span>
                    <span className="time-info">13:37</span>
                  </div>
                )}
                <div className="status-info">
                  <span className="reds-info">{redsRemaining}</span>
                  <span className="next-ball">{lastBallPotted === null ? 'REDS' : lastBallPotted === 'red' ? 'COLOR' : 'REDS'}</span>
                </div>
              </div>
            </div>

            {/* Player B Section */}
            <div 
              className={`player-section player-b ${currentPlayer === 'B' ? 'active' : ''}`}
              onClick={() => currentPlayer !== 'B' && !isFrameComplete && switchPlayer()}
              style={{ cursor: currentPlayer !== 'B' && !isFrameComplete ? 'pointer' : 'default' }}>
              <div className="player-name">
                <span className="name">{game.playerB.preName} {game.playerB.name}</span>
              </div>
              <div className="current-score">{frameScoreB}</div>
              <div className="break-info">
                <span className="break-label">Break</span>
                <span className="break-score">{currentPlayer === 'B' ? currentBreakScore : 0}</span>
              </div>
              {currentPlayer === 'B' && currentBreak.length > 0 && (
                <div className="break-visual">
                  <div className="break-balls-row">
                    {currentBreak.map((ball, index) => {
                      const isFoul = ball < 0;
                      const ballValue = Math.abs(ball);
                      const ballColor = ballValue === 1 ? 'red' : ballValue === 2 ? 'yellow' : ballValue === 3 ? 'green' : ballValue === 4 ? 'brown' : ballValue === 5 ? 'blue' : ballValue === 6 ? 'pink' : 'black';
                      return (
                        <div 
                          key={index} 
                          className={`break-ball-small ${isFoul ? `foul-${ballColor}` : ballColor}`}
                        >
                          {isFoul ? 'F' : ballValue}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="player-stats">
                <div className="stat-item">
                  <span className="stat-label">Highbreak</span>
                  <span className="stat-value">{highestBreakB}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Points</span>
                  <span className="stat-value">{frameScoreB}</span>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Points Section */}
        <div className="points-section">
          <div className="points-grid">
            {[
              { value: 1, color: 'red', name: 'Red' },
              { value: 2, color: 'yellow', name: 'Yellow' },
              { value: 3, color: 'green', name: 'Green' },
              { value: 4, color: 'brown', name: 'Brown' },
              { value: 5, color: 'blue', name: 'Blue' },
              { value: 6, color: 'pink', name: 'Pink' },
              { value: 7, color: 'black', name: 'Black' }
            ].map(ball => {
              const isRedBall = ball.value === 1;
              const isColoredBall = ball.value >= 2 && ball.value <= 7;
              
              // Determine if this ball can be potted
              let canPot = false;
              if (!breakEnded) {
                if (isRedBall) {
                  canPot = lastBallPotted === 'colored' || lastBallPotted === null || redsRemaining === 0;
                } else if (isColoredBall) {
                  canPot = lastBallPotted === 'red' || redsRemaining === 0;
                }
              }
              
              return (
                <button
                  key={ball.value}
                  onClick={() => addPoints(ball.value)}
                  className={`points-btn points-btn-${ball.color} ${!canPot ? 'disabled' : ''}`}
                  disabled={isFrameComplete || !canPot}
                  title={`${ball.name} ${!canPot ? '(Invalid shot)' : ''}`}
                >
                  {ball.value}
                </button>
              );
            })}
          </div>
          
          {/* Foul Points */}
          <div className="foul-grid">
            {[
              { value: 1, color: 'red', name: 'Red Foul' },
              { value: 2, color: 'yellow', name: 'Yellow Foul' },
              { value: 3, color: 'green', name: 'Green Foul' },
              { value: 4, color: 'brown', name: 'Brown Foul' },
              { value: 5, color: 'blue', name: 'Blue Foul' },
              { value: 6, color: 'pink', name: 'Pink Foul' },
              { value: 7, color: 'black', name: 'Black Foul' }
            ].map(ball => (
              <button
                key={`foul-${ball.value}`}
                onClick={() => addFoulPoints(ball.value)}
                className={`foul-btn foul-btn-${ball.color}`}
                disabled={isFrameComplete || breakEnded}
                title={ball.name}
              >
                F
              </button>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons-compact">
          <button
            onClick={undoLastShot}
            className="btn btn-secondary btn-small"
            disabled={!canUndo}
            title="Undo Last Shot"
          >
            ↶ Undo
          </button>
          <button
            onClick={switchPlayer}
            className="btn btn-primary btn-small"
            disabled={isFrameComplete}
          >
            Switch Player
          </button>
          <button
            onClick={completeFrame}
            className="btn btn-primary btn-large"
            disabled={isFrameComplete || saving || frameScoreA === frameScoreB}
            title={frameScoreA === frameScoreB ? 'Cannot complete frame with equal scores' : ''}
          >
            {saving ? 'Saving...' : 'Complete Frame'}
          </button>
        </div>
      </div>


      {/* Tie Message */}
      {frameScoreA === frameScoreB && frameScoreA > 0 && (
        <div className="frame-tie-message">
          Frame tied at {frameScoreA}-{frameScoreB}. Continue playing to break the tie.
        </div>
      )}

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {/* Break History Modal */}
      {showBreakHistory && (
        <div className="modal-overlay" onClick={() => setShowBreakHistory(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Break History</h2>
              <button 
                className="modal-close" 
                onClick={() => setShowBreakHistory(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {breaks.length === 0 ? (
                <p className="no-breaks">No breaks recorded yet</p>
              ) : (
                <div className="breaks-list">
                  {breaks.map((breakItem, index) => (
                    <div key={index} className="break-item">
                      <div className="break-header">
                        <span className="break-player">
                          {breakItem.player === 'A' ? game!.playerA.preName : game!.playerB.preName}
                        </span>
                        <span className="break-score">{breakItem.score}</span>
                        <span className="break-time">
                          {breakItem.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="break-balls">
                        {breakItem.balls.map((ball, ballIndex) => {
                          const isFoul = ball < 0;
                          const ballValue = Math.abs(ball);
                          const ballColor = ballValue === 1 ? 'red' : ballValue === 2 ? 'yellow' : ballValue === 3 ? 'green' : ballValue === 4 ? 'brown' : ballValue === 5 ? 'blue' : ballValue === 6 ? 'pink' : 'black';
                          
                          // For fouls, show "F" instead of numbers
                          const displayValue = isFoul ? 'F' : ballValue;
                          const foulPoints = isFoul ? (ballValue <= 4 ? 4 : ballValue) : ballValue;
                          
                          return (
                            <span 
                              key={ballIndex} 
                              className={`break-ball ${isFoul ? `break-ball-foul-${ballColor}` : `break-ball-${ballColor}`}`}
                              title={isFoul ? `Foul: ${foulPoints} points` : `${ballValue} points`}
                            >
                              {displayValue}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GamePlay;
